import React, { useContext, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { AuthContext } from "../context/AuthContext";
import "./PathwayTab.css";

const ROLE_OPTIONS = [
  "Keluarga Petani",
  "Penggerak Pemuda",
  "Agripreneur",
  "Fasilitator Lapangan",
  "Supervisor",
];

const TIMELINES = ["3 Tahun Pertama", "3 Tahun Kedua", "3 Tahun Ketiga", "Opsional"];
const TYPES = ["Fungsional", "Kepemimpinan"];
const FUNCTIONAL_STANDARDS = ["Esensial", "Advance", "Expert"];
const LEADERSHIP_STANDARDS = ["Tidak Terampil", "Terampil", "Berlebihan"];

const normalizeCapType = (v) => {
  const s = String(v || "").trim().toLowerCase();
  if (!s) return "";
  if (s.includes("kepem")) return "kepemimpinan";
  return "fungsional";
};

export default function PathwayTab() {
  const { user } = useContext(AuthContext);
  const isAdmin = String(user?.role || "").toLowerCase() === "admin";
  const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:3000/api";
  const getStandardsByType = (type) =>
    type === "Kepemimpinan" ? LEADERSHIP_STANDARDS : FUNCTIONAL_STANDARDS;

  // From-To
  const [items, setItems] = useState([]);
  const [loadingLeft, setLoadingLeft] = useState(true);

  // Grid kompetensi per role
  const [grid, setGrid] = useState(null);
  const [loadingRight, setLoadingRight] = useState(false);

  // Semua kompetensi utk dropdown
  const [allComps, setAllComps] = useState([]);
  const [loadingAll, setLoadingAll] = useState(false);

  // filter role
  const [roleFilter, setRoleFilter] = useState("");

  // modal tambah/edit From-To
  const [openFT, setOpenFT] = useState(false);
  const [savingFT, setSavingFT] = useState(false);
  const [isEditFT, setIsEditFT] = useState(false);
  const [editIdFT, setEditIdFT] = useState(null);
  const [formFT, setFormFT] = useState({ role: ROLE_OPTIONS[0], from: "", to: "" });

  // modal tambah kompetensi per timeline (+ standar)
  const [addC, setAddC] = useState({
    open: false,
    mode: "add",
    type: "Fungsional",
    timeline: TIMELINES[0],
    originalCompId: "",
    compId: "",
    standard: "Esensial",
    editing: true,
  });
  const [savingC, setSavingC] = useState(false);

  // Fetch From-To
  const fetchFromTo = async (role = "") => {
    try {
      setLoadingLeft(true);
      const res = await axios.get(`${apiUrl}/pathway`, { params: role ? { role } : {} });
      setItems(res?.data?.data || []);
    } catch (e) {
      console.error("Load pathway error:", e);
      setItems([]);
    } finally {
      setLoadingLeft(false);
    }
  };

  // Fetch grid mapping per role
  const fetchGrid = async (role) => {
    if (!role) {
      setGrid(null);
      return;
    }
    try {
      setLoadingRight(true);
      const res = await axios.get(`${apiUrl}/capdev/competencies/by-role-timeline`, { params: { role } });
      const rows = res?.data?.data || [];
      const bucket = {};
      TYPES.forEach((t) => {
        bucket[t] = {};
        TIMELINES.forEach((tl) => (bucket[t][tl] = []));
      });
      rows.forEach((r) => {
        const t = TYPES.includes(r.type) ? r.type : "Fungsional";
        const tl = TIMELINES.includes(r.timeline) ? r.timeline : "Opsional";
        bucket[t][tl].push({ id: r.id, name: r.name, standard: r.standard });
      });
      setGrid(bucket);
    } catch (e) {
      console.error("Load competencies-by-role error:", e);
      setGrid(null);
    } finally {
      setLoadingRight(false);
    }
  };

  // Fetch semua kompetensi untuk dropdown
  const fetchAllComps = async () => {
    try {
      setLoadingAll(true);
      const res = await axios.get(`${apiUrl}/capdev/competencies/all`);
      setAllComps(res?.data?.data || []);
    } catch (e) {
      console.error("Load all competencies error:", e);
      setAllComps([]);
    } finally {
      setLoadingAll(false);
    }
  };

  useEffect(() => {
    fetchFromTo(roleFilter);
    fetchGrid(roleFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleFilter, apiUrl]);

  useEffect(() => {
    fetchAllComps();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiUrl]);

  // From-To submit (tambah / edit)
  const validFT = formFT.from.trim() && formFT.to.trim();
  const submitFT = async () => {
    if (!validFT || savingFT) return;
    setSavingFT(true);
    try {
      let res;
      if (isEditFT && editIdFT) {
        res = await axios.put(`${apiUrl}/pathway/${editIdFT}`, {
          ...formFT,
          user_role: user?.role,
        });
      } else {
        res = await axios.post(`${apiUrl}/pathway`, {
          ...formFT,
          created_by: user?.id,
          user_role: user?.role,
        });
      }

      if (res?.data?.success) {
        await fetchFromTo(roleFilter);
        setOpenFT(false);
        setIsEditFT(false);
        setEditIdFT(null);
        setFormFT({ role: roleFilter || ROLE_OPTIONS[0], from: "", to: "" });
      } else {
        alert(res?.data?.message || (isEditFT ? "Gagal mengubah pathway." : "Gagal menambahkan pathway."));
      }
    } catch (e) {
      if (e?.response?.status === 403) alert("Hanya admin yang dapat mengelola data.");
      else alert(isEditFT ? "Gagal mengubah pathway." : "Gagal menambahkan pathway.");
    } finally {
      setSavingFT(false);
    }
  };

  // Add mapping competency
  const openAddComp = (type, timeline) => {
    const standards = getStandardsByType(type);
    setAddC({
      open: true,
      mode: "add",
      type,
      timeline,
      originalCompId: "",
      compId: "",
      standard: standards[0],
      editing: true,
    });
  };
  const openEditComp = (type, timeline, comp) => {
    setAddC({
      open: true,
      mode: "edit",
      type,
      timeline,
      originalCompId: String(comp?.id || ""),
      compId: String(comp?.id || ""),
      standard: comp?.standard || getStandardsByType(type)[0],
      editing: false,
    });
  };
  const submitAddComp = async () => {
    if (!roleFilter) return alert("Pilih role terlebih dahulu.");
    if (!addC.compId || savingC) return;
    setSavingC(true);
    try {
      const payload = addC.mode === "edit"
        ? {
            role: roleFilter,
            timeline: addC.timeline,
            old_competency_id: Number(addC.originalCompId),
            competency_id: Number(addC.compId),
            standard: addC.standard,
            user_role: user?.role,
          }
        : {
            role: roleFilter,
            timeline: addC.timeline,
            competency_id: Number(addC.compId),
            standard: addC.standard,
            user_role: user?.role,
          };
      const res = addC.mode === "edit"
        ? await axios.put(`${apiUrl}/capdev/role-timeline-map`, payload)
        : await axios.post(`${apiUrl}/capdev/role-timeline-map`, payload);
      if (res?.data?.success) {
        await fetchGrid(roleFilter);
        setAddC({
          open: false,
          mode: "add",
          type: "Fungsional",
          timeline: TIMELINES[0],
          originalCompId: "",
          compId: "",
          standard: FUNCTIONAL_STANDARDS[0],
          editing: true,
        });
      } else {
        alert(res?.data?.message || (addC.mode === "edit"
          ? "Gagal mengubah kompetensi pada timeline."
          : "Gagal menambahkan kompetensi ke timeline."));
      }
    } catch (e) {
      if (e?.response?.status === 403) {
        alert(addC.mode === "edit"
          ? "Hanya admin yang dapat mengubah pemetaan."
          : "Hanya admin yang dapat menambah pemetaan.");
      } else {
        alert(addC.mode === "edit"
          ? "Gagal mengubah kompetensi pada timeline."
          : "Gagal menambahkan kompetensi ke timeline.");
      }
    } finally {
      setSavingC(false);
    }
  };
  const removeComp = async (timeline, compId) => {
    if (!roleFilter || !isAdmin) return;
    try {
      await axios.delete(`${apiUrl}/capdev/role-timeline-map`, {
        params: { role: roleFilter, timeline, competency_id: compId, user_role: user?.role },
      });
      await fetchGrid(roleFilter);
    } catch (e) {
      alert("Gagal menghapus pemetaan.");
    }
  };

  // Opsi dropdown = semua kompetensi dengan type sama dan belum ada di sel
  const dropdownOptions = useMemo(() => {
    if (!addC.open) return [];
    const type = addC.type;
    const wantedType = normalizeCapType(type);
    const already = new Set((grid?.[type]?.[addC.timeline] || []).map((x) => Number(x.id)));
    return allComps.filter((c) => {
      if (normalizeCapType(c.type) !== wantedType) return false;
      const id = Number(c.id);
      if (addC.mode === "edit" && id === Number(addC.originalCompId)) return true;
      return !already.has(id);
    });
  }, [addC, allComps, grid]);

  const fromToData = useMemo(() => items, [items]);

  const closeFT = () => {
    if (savingFT) return;
    setOpenFT(false);
    setIsEditFT(false);
    setEditIdFT(null);
    setFormFT({ role: roleFilter || ROLE_OPTIONS[0], from: "", to: "" });
  };
  const openAddFT = () => {
    if (!roleFilter) {
      alert("Pilih role terlebih dahulu agar From-To tersimpan sesuai peran.");
      return;
    }
    setIsEditFT(false);
    setEditIdFT(null);
    setFormFT({ role: roleFilter, from: "", to: "" });
    setOpenFT(true);
  };

  const openEditFT = () => {
    if (!roleFilter) {
      alert("Pilih role terlebih dahulu agar edit sesuai masing-masing peran.");
      return;
    }
    const row = fromToData.find((x) => x.role === roleFilter) || null;
    if (!row) {
      alert("Data From-To untuk role ini belum ada.");
      return;
    }
    setIsEditFT(true);
    setEditIdFT(row.id);
    setFormFT({ role: roleFilter, from: row.from || "", to: row.to || "" });
    setOpenFT(true);
  };

  const deleteFromTo = async () => {
    if (!roleFilter) {
      alert("Pilih role terlebih dahulu agar hapus sesuai masing-masing peran.");
      return;
    }

    const row = fromToData.find((x) => x.role === roleFilter) || null;
    if (!row) {
      alert("Data From-To untuk role ini belum ada.");
      return;
    }

    const ok = window.confirm("Hapus data From-To untuk role ini?");
    if (!ok) return;

    try {
      const res = await axios.delete(`${apiUrl}/pathway/${row.id}`, {
        data: { user_role: user?.role },
      });
      if (res?.data?.success) {
        await fetchFromTo(roleFilter);
        if (isEditFT) {
          setOpenFT(false);
          setIsEditFT(false);
          setEditIdFT(null);
          setFormFT({ role: roleFilter || ROLE_OPTIONS[0], from: "", to: "" });
        }
      } else {
        alert(res?.data?.message || "Gagal menghapus From-To.");
      }
    } catch (e) {
      if (e?.response?.status === 403) alert("Hanya admin yang dapat menghapus data.");
      else alert(e?.response?.data?.message || "Gagal menghapus From-To.");
    }
  };

  const renderModal = (content) => createPortal(content, document.body);


  return (
    <div className="pathway-wrap">
      <div className="pathway-sticky">
        <div className="pathway-bar">
          <div className="left">
            <label className="filter">
              <span>Apa Peran Anda di Komunitas?</span>
              <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
                <option value="">Semua Role</option>
                {ROLE_OPTIONS.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </label>
          </div>
          <div className="right">
            {isAdmin && (
              <>
                <button className="btn-del" onClick={deleteFromTo}>Delete From-To</button>
                <button className="btn-alt" onClick={openEditFT}>Edit From-To</button>
                <button className="btn" onClick={openAddFT}>+ From-To</button>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="two-col">
        {/* kiri: From-To */}
        <div className="col">
          <div className="table-title">Transformasi Pengembangan Diri</div>
          {loadingLeft ? (
            <div className="muted">Memuat...</div>
          ) : fromToData.length === 0 ? (
            <div className="muted">Belum ada data.</div>
          ) : (
            <table className="pathway-table">
              <thead><tr><th>Dari</th><th>Menjadi</th></tr></thead>
              <tbody>
                {fromToData.map((row) => (
                  <tr key={row.id}>
                    <td className="pre">{row.from}</td>
                    <td className="pre">{row.to}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* kanan: grid timeline */}
        <div className="col">
          <div className="table-title">Tahapan Pengembangan Diri</div>
          {!roleFilter ? (
            <div className="muted">Pilih role untuk melihat & mengatur kompetensi per timeline.</div>
          ) : loadingRight ? (
            <div className="muted">Memuat...</div>
          ) : !grid ? (
            <div className="muted">Belum ada data.</div>
          ) : (
            <table className="skills-table">
              <thead>
                <tr>{TIMELINES.map((t) => (<th key={t}>{t}</th>))}</tr>
              </thead>
              <tbody>
                {/* Fungsional */}
                <tr><th className="section" colSpan={4}>Fungsional</th></tr>
                <tr>
                  {TIMELINES.map((tl) => (
                    <td key={`F-${tl}`} className="pre">
                      <ul className="list-tight">
                        {(grid["Fungsional"][tl] || []).map((c) => (
                          <li key={c.id} className="li-flex">
                            <span
                              className={isAdmin ? "pathway-comp-link" : ""}
                              onClick={isAdmin ? () => openEditComp("Fungsional", tl, c) : undefined}
                              role={isAdmin ? "button" : undefined}
                              tabIndex={isAdmin ? 0 : undefined}
                              onKeyDown={isAdmin ? (e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  openEditComp("Fungsional", tl, c);
                                }
                              } : undefined}
                            >
                              {c.name}
                            </span>
                            {isAdmin && (
                              <button className="mini-del" title="Hapus"
                                onClick={() => removeComp(tl, c.id)}>x</button>
                            )}
                          </li>
                        ))}
                      </ul>
                      {isAdmin && (
                        <button className="mini-add" onClick={() => openAddComp("Fungsional", tl)}>
                          + Tambah
                        </button>
                      )}
                    </td>
                  ))}
                </tr>

                {/* Kepemimpinan */}
                <tr><th className="section" colSpan={4}>Kepemimpinan</th></tr>
                <tr>
                  {TIMELINES.map((tl) => (
                    <td key={`K-${tl}`} className="pre">
                      <ul className="list-tight">
                        {(grid["Kepemimpinan"][tl] || []).map((c) => (
                          <li key={c.id} className="li-flex">
                            <span
                              className={isAdmin ? "pathway-comp-link" : ""}
                              onClick={isAdmin ? () => openEditComp("Kepemimpinan", tl, c) : undefined}
                              role={isAdmin ? "button" : undefined}
                              tabIndex={isAdmin ? 0 : undefined}
                              onKeyDown={isAdmin ? (e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  openEditComp("Kepemimpinan", tl, c);
                                }
                              } : undefined}
                            >
                              {c.name}
                            </span>
                            {isAdmin && (
                              <button className="mini-del" title="Hapus"
                                onClick={() => removeComp(tl, c.id)}>x</button>
                            )}
                          </li>
                        ))}
                      </ul>
                      {isAdmin && (
                        <button className="mini-add" onClick={() => openAddComp("Kepemimpinan", tl)}>
                          + Tambah
                        </button>
                      )}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Modal From-To */}
      {openFT && renderModal(
        <div className="pathway-modal-backdrop" onClick={closeFT}>
          <div className="pathway-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pathway-modal-head">
              <h4>{isEditFT ? "Edit From-To" : "Tambah From-To"}</h4>
              <button className="x" onClick={closeFT}>x</button>
            </div>
            <div className="pathway-form">
              <label>
                <span>Role</span>
                <select value={formFT.role} disabled={isEditFT} onChange={(e) => setFormFT({ ...formFT, role: e.target.value })}>
                  {ROLE_OPTIONS.map((r) => (<option key={r} value={r}>{r}</option>))}
                </select>
              </label>
              <div className="pathway-grid-2">
                <label><span>Dari</span>
                  <textarea rows={4} value={formFT.from}
                    onChange={(e) => setFormFT({ ...formFT, from: e.target.value })} />
                </label>
                <label><span>Menjadi</span>
                  <textarea rows={4} value={formFT.to}
                    onChange={(e) => setFormFT({ ...formFT, to: e.target.value })} />
                </label>
              </div>
            </div>
            <div className="pathway-modal-actions">
              <button className="btn-alt" onClick={closeFT}>Batal</button>
              <button className="btn" onClick={submitFT} disabled={!validFT || savingFT}>
                {savingFT ? "Menyimpan..." : isEditFT ? "Update" : "Simpan"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal tambah kompetensi ke timeline (dengan STANDAR) */}
      {addC.open && renderModal(
        <div className="pathway-modal-backdrop" onClick={() => !savingC && setAddC({ ...addC, open: false, editing: addC.mode === "add" })}>
          <div className="pathway-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pathway-modal-head">
              <h4>{addC.mode === "edit" ? "Edit Kompetensi" : "Tambah Kompetensi"} - {addC.type} / {addC.timeline}</h4>
              <button className="x" onClick={() => !savingC && setAddC({ ...addC, open: false, editing: addC.mode === "add" })}>x</button>
            </div>
            <div className="pathway-form">
              <label>
                <span>Pilih Kompetensi ({loadingAll ? "memuat..." : `${dropdownOptions.length} opsi`})</span>
                <select
                  value={addC.compId}
                  disabled={addC.mode === "edit" && !addC.editing}
                  onChange={(e) => setAddC({ ...addC, compId: e.target.value })}
                >
                  <option value="">-- pilih --</option>
                  {dropdownOptions.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </label>

              <label>
                <span>Standar Kompetensi</span>
                <select
                  value={addC.standard}
                  disabled={addC.mode === "edit" && !addC.editing}
                  onChange={(e) => setAddC({ ...addC, standard: e.target.value })}
                >
                  {getStandardsByType(addC.type).map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div className="pathway-modal-actions">
              <button className="btn-alt" onClick={() => !savingC && setAddC({ ...addC, open: false, editing: addC.mode === "add" })}>Batal</button>
              {addC.mode === "edit" && (
                <button className="btn-alt" onClick={() => setAddC({ ...addC, editing: true })} disabled={savingC || addC.editing}>
                  Edit
                </button>
              )}
              <button className="btn" onClick={submitAddComp} disabled={savingC || !addC.compId || (addC.mode === "edit" && !addC.editing)}>
                {savingC ? "Menyimpan..." : "Simpan"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}






















